import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { EscalarModal, type EscalarScope } from './EscalarModal';

interface EscalarContextValue {
  openEscalar: (scope: EscalarScope) => void;
  closeEscalar: () => void;
}

const EscalarContext = createContext<EscalarContextValue | null>(null);

export function EscalarModalProvider({ children }: { children: ReactNode }) {
  const [scope, setScope] = useState<EscalarScope | null>(null);

  const openEscalar = useCallback((next: EscalarScope) => {
    setScope(next);
  }, []);
  const closeEscalar = useCallback(() => {
    setScope(null);
  }, []);

  const value = useMemo<EscalarContextValue>(
    () => ({ openEscalar, closeEscalar }),
    [openEscalar, closeEscalar]
  );

  return (
    <EscalarContext.Provider value={value}>
      {children}
      <EscalarModal
        open={scope !== null}
        onClose={closeEscalar}
        scope={scope ?? { type: 'bulk', cluster: '', items: [] }}
      />
    </EscalarContext.Provider>
  );
}

export function useEscalarModal(): EscalarContextValue {
  const ctx = useContext(EscalarContext);
  if (!ctx) {
    return {
      openEscalar: () => {
        /* noop when provider absent */
      },
      closeEscalar: () => {
        /* noop when provider absent */
      },
    };
  }
  return ctx;
}

export default EscalarModalProvider;
